import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  Building2, Users, DollarSign, CheckSquare, Plus, Trash2, 
  Edit, Phone, Mail, Calendar, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { UpgradeModal } from '../components/UpgradeModal';
import { useSubscription } from '../hooks/useSubscription';
import PWAInstallPrompt from '../components/PWAInstallPrompt';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DigiCRM() {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [companies, setCompanies] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [pipelines, setPipelines] = useState([]);
  const [deals, setDeals] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Subscription management
  const { canUse, limit, showUpgradeModal, setShowUpgradeModal, checkAndIncrement} = useSubscription('crm_accounts');

  // Email states
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [newTemplate, setNewTemplate] = useState({ name: '', subject: '', body: '' });
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [allContacts, setAllContacts] = useState([]);
  const [gmailUser, setGmailUser] = useState('');

  // Form states
  const [newCompany, setNewCompany] = useState({ name: '', domain: '', phone: '' });
  const [newContact, setNewContact] = useState({ 
    firstName: '', lastName: '', email: '', phone: '', companyId: '', lifecycleStage: 'lead'
  });
  const [newDeal, setNewDeal] = useState({ 
    title: '', amount: '', currency: 'USD', pipelineId: '', stage: 'open', contactId: '', companyId: ''
  });
  const [newTask, setNewTask] = useState({ title: '', dueDate: '', relatedType: '', relatedId: '' });
  const [newPipelineName, setNewPipelineName] = useState('');

  useEffect(() => {
    loadAllData();
  }, []);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    };
  };

  const loadAllData = async () => {
    setLoading(true);
    try {
      // Load from localStorage (My Network contacts)
      const savedContacts = JSON.parse(localStorage.getItem('scannedContacts') || '[]');
      setAllContacts(savedContacts);
      
      const authHeaders = getAuthHeaders();
      
      // Load all data with individual error handling
      const results = await Promise.allSettled([
        axios.get(`${API}/crm/companies`, authHeaders),
        axios.get(`${API}/crm/contacts`, authHeaders),
        axios.get(`${API}/crm/pipelines`, authHeaders),
        axios.get(`${API}/crm/deals`, authHeaders),
        axios.get(`${API}/crm/tasks`, authHeaders),
        axios.get(`${API}/crm/email-templates`, authHeaders)
      ]);
      
      // Extract data with fallbacks
      const [companiesRes, contactsRes, pipelinesRes, dealsRes, tasksRes, templatesRes] = results;
      
      setCompanies(companiesRes.status === 'fulfilled' ? (companiesRes.value.data.companies || []) : []);
      setContacts(contactsRes.status === 'fulfilled' ? (contactsRes.value.data.contacts || []) : []);
      setPipelines(pipelinesRes.status === 'fulfilled' ? (pipelinesRes.value.data.pipelines || []) : []);
      setDeals(dealsRes.status === 'fulfilled' ? (dealsRes.value.data.deals || []) : []);
      setTasks(tasksRes.status === 'fulfilled' ? (tasksRes.value.data.tasks || []) : []);
      setEmailTemplates(templatesRes.status === 'fulfilled' ? (templatesRes.value.data.templates || []) : []);
      
      // Log any failures for debugging
      results.forEach((result, index) => {
        if (result.status === 'rejected') {
          const endpoints = ['companies', 'contacts', 'pipelines', 'deals', 'tasks', 'email-templates'];
          console.warn(`Failed to load ${endpoints[index]}:`, result.reason);
        }
      });
      
    } catch (error) {
      console.error('Failed to load CRM data:', error);
      toast.error('Failed to load CRM data');
    } finally {
      setLoading(false);
    }
  };

  const createCompany = async (e) => {
    e.preventDefault();
    if (!newCompany.name) {
      toast.error('Company name is required');
      return;
    }

    // Check subscription limit
    const allowed = await checkAndIncrement();
    if (!allowed) {
      return; // Modal will show automatically
    }

    try {
      await axios.post(`${API}/crm/companies`, newCompany);
      toast.success('Company created!');
      setNewCompany({ name: '', domain: '', phone: '' });
      loadAllData();
    } catch (error) {
      toast.error('Failed to create company');
    }
  };

  const createContact = async (e) => {
    e.preventDefault();
    if (!newContact.firstName && !newContact.lastName) {
      toast.error('First or last name is required');
      return;
    }

    try {
      await axios.post(`${API}/crm/contacts`, newContact);
      toast.success('Contact created!');
      setNewContact({ firstName: '', lastName: '', email: '', phone: '', companyId: '', lifecycleStage: 'lead' });
      loadAllData();
    } catch (error) {
      toast.error('Failed to create contact');
    }
  };

  const createDeal = async (e) => {
    e.preventDefault();
    if (!newDeal.title) {
      toast.error('Deal title is required');
      return;
    }

    try {
      await axios.post(`${API}/crm/deals`, {
        ...newDeal,
        amount: parseFloat(newDeal.amount || 0)
      });
      toast.success('Deal created!');
      setNewDeal({ title: '', amount: '', currency: 'USD', pipelineId: '', stage: 'open', contactId: '', companyId: '' });
      loadAllData();
    } catch (error) {
      toast.error('Failed to create deal');
    }
  };

  const createTask = async (e) => {
    e.preventDefault();
    if (!newTask.title) {
      toast.error('Task title is required');
      return;
    }

    try {
      await axios.post(`${API}/crm/tasks`, newTask);
      toast.success('Task created!');
      setNewTask({ title: '', dueDate: '', relatedType: '', relatedId: '' });
      loadAllData();
    } catch (error) {
      toast.error('Failed to create task');
    }
  };

  const createPipeline = async () => {
    if (!newPipelineName) {
      toast.error('Pipeline name is required');
      return;
    }

    try {
      await axios.post(`${API}/crm/pipelines`, { name: newPipelineName });
      toast.success('Pipeline created!');
      setNewPipelineName('');
      loadAllData();
    } catch (error) {
      toast.error('Failed to create pipeline');
    }
  };

  const deleteItem = async (type, id) => {
    if (!window.confirm(`Delete this ${type}?`)) return;

    try {
      await axios.delete(`${API}/crm/${type}/${id}`);
      toast.success(`${type} deleted!`);
      loadAllData();
    } catch (error) {
      toast.error(`Failed to delete ${type}`);
    }
  };

  const toggleTask = async (task) => {
    try {
      await axios.put(`${API}/crm/tasks/${task.id}`, { done: !task.done });
      loadAllData();
    } catch (error) {
      toast.error('Failed to update task');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8 space-y-4">
          <div>
            <h1 className="text-4xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent mb-2">
              DigiCRM
            </h1>
            <p className="text-gray-600">Comprehensive CRM for managing your business relationships</p>
          </div>
          
          {/* About Section */}
          <div className="bg-gradient-to-r from-purple-50 to-pink-50 border-2 border-purple-200 rounded-lg p-4">
            <h3 className="font-semibold text-gray-900 mb-2">About DigiCRM</h3>
            <p className="text-sm text-gray-700 leading-relaxed">
              Manage your entire sales pipeline with DigiCRM. Track companies, contacts, deals, and tasks all in one place. 
              Create custom pipelines, monitor deal progress, and never miss a follow-up. Perfect for small businesses 
              and growing teams who need a simple yet powerful CRM solution.
            </p>
          </div>
          {limit && !limit.allowed && (
            <p className="text-sm text-orange-600 mt-2 font-semibold">
              ⚠️ You've reached your limit ({limit.current}/{limit.max} accounts). 
              <a href="/pricing" className="underline ml-1">Upgrade to add more</a>
            </p>
          )}
          {limit && limit.allowed && limit.max !== 'unlimited' && (
            <p className="text-sm text-gray-500 mt-2">
              {limit.remaining} of {limit.max} accounts remaining
            </p>
          )}
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-gray-200 overflow-x-auto">
          {['dashboard', 'companies', 'contacts', 'deals', 'tasks', 'pipelines', 'bulk-email'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-6 py-3 font-medium transition-colors capitalize whitespace-nowrap ${
                activeTab === tab
                  ? 'text-purple-600 border-b-2 border-purple-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Refresh Button */}
        <div className="mb-4 flex justify-end">
          <Button onClick={loadAllData} variant="outline" disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-purple-100 text-sm">Companies</p>
                      <h3 className="text-3xl font-bold mt-2">{companies.length}</h3>
                    </div>
                    <Building2 className="w-12 h-12 text-purple-200" />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-pink-500 to-pink-600 text-white">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-pink-100 text-sm">Contacts</p>
                      <h3 className="text-3xl font-bold mt-2">{contacts.length}</h3>
                    </div>
                    <Users className="w-12 h-12 text-pink-200" />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-indigo-500 to-indigo-600 text-white">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-indigo-100 text-sm">Deals</p>
                      <h3 className="text-3xl font-bold mt-2">{deals.length}</h3>
                    </div>
                    <DollarSign className="w-12 h-12 text-indigo-200" />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-teal-500 to-teal-600 text-white">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-teal-100 text-sm">Tasks</p>
                      <h3 className="text-3xl font-bold mt-2">{tasks.filter(t => !t.done).length}</h3>
                    </div>
                    <CheckSquare className="w-12 h-12 text-teal-200" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Quick Add Forms */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Add Company */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Building2 className="w-5 h-5" />
                    Add Company
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <form onSubmit={createCompany} className="space-y-3">
                    <Input
                      placeholder="Company name *"
                      value={newCompany.name}
                      onChange={(e) => setNewCompany({ ...newCompany, name: e.target.value })}
                    />
                    <Input
                      placeholder="Domain (optional)"
                      value={newCompany.domain}
                      onChange={(e) => setNewCompany({ ...newCompany, domain: e.target.value })}
                    />
                    <Input
                      placeholder="Phone (optional)"
                      value={newCompany.phone}
                      onChange={(e) => setNewCompany({ ...newCompany, phone: e.target.value })}
                    />
                    <Button type="submit" className="w-full bg-purple-600 hover:bg-purple-700">
                      Create Company
                    </Button>
                  </form>
                </CardContent>
              </Card>

              {/* Add Contact */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Users className="w-5 h-5" />
                    Add Contact
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <form onSubmit={createContact} className="space-y-3">
                    <Input
                      placeholder="First name"
                      value={newContact.firstName}
                      onChange={(e) => setNewContact({ ...newContact, firstName: e.target.value })}
                    />
                    <Input
                      placeholder="Last name"
                      value={newContact.lastName}
                      onChange={(e) => setNewContact({ ...newContact, lastName: e.target.value })}
                    />
                    <Input
                      type="email"
                      placeholder="Email"
                      value={newContact.email}
                      onChange={(e) => setNewContact({ ...newContact, email: e.target.value })}
                    />
                    <select
                      value={newContact.companyId}
                      onChange={(e) => setNewContact({ ...newContact, companyId: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    >
                      <option value="">No company</option>
                      {companies.map((c) => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                    <Button type="submit" className="w-full bg-pink-600 hover:bg-pink-700">
                      Create Contact
                    </Button>
                  </form>
                </CardContent>
              </Card>

              {/* Add Deal */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <DollarSign className="w-5 h-5" />
                    Add Deal
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <form onSubmit={createDeal} className="space-y-3">
                    <Input
                      placeholder="Deal title *"
                      value={newDeal.title}
                      onChange={(e) => setNewDeal({ ...newDeal, title: e.target.value })}
                    />
                    <Input
                      type="number"
                      placeholder="Amount"
                      value={newDeal.amount}
                      onChange={(e) => setNewDeal({ ...newDeal, amount: e.target.value })}
                    />
                    <select
                      value={newDeal.pipelineId}
                      onChange={(e) => setNewDeal({ ...newDeal, pipelineId: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    >
                      <option value="">Select pipeline</option>
                      {pipelines.map((p) => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                      ))}
                    </select>
                    <Button type="submit" className="w-full bg-indigo-600 hover:bg-indigo-700">
                      Create Deal
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>

            {/* Recent Activity */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Recent Contacts</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 max-h-64 overflow-auto">
                    {contacts.slice(0, 5).map((contact) => (
                      <div key={contact.id} className="p-3 border rounded-lg hover:bg-gray-50">
                        <p className="font-medium">{contact.firstName} {contact.lastName}</p>
                        <p className="text-sm text-gray-600">{contact.email}</p>
                        {contact.companyName && (
                          <p className="text-xs text-gray-500">{contact.companyName}</p>
                        )}
                      </div>
                    ))}
                    {contacts.length === 0 && (
                      <p className="text-gray-500 text-center py-4">No contacts yet</p>
                    )}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Recent Deals</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 max-h-64 overflow-auto">
                    {deals.slice(0, 5).map((deal) => (
                      <div key={deal.id} className="p-3 border rounded-lg hover:bg-gray-50">
                        <p className="font-medium">{deal.title}</p>
                        <p className="text-sm text-gray-600">${deal.amount} {deal.currency}</p>
                        {deal.companyName && (
                          <p className="text-xs text-gray-500">{deal.companyName}</p>
                        )}
                      </div>
                    ))}
                    {deals.length === 0 && (
                      <p className="text-gray-500 text-center py-4">No deals yet</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* Companies Tab */}
        {activeTab === 'companies' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle>All Companies ({companies.length})</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 max-h-[600px] overflow-auto">
                  {companies.map((company) => (
                    <div key={company.id} className="p-4 border rounded-lg flex items-start justify-between hover:bg-gray-50">
                      <div className="flex-1">
                        <h4 className="font-semibold text-lg">{company.name}</h4>
                        {company.domain && (
                          <p className="text-sm text-gray-600">{company.domain}</p>
                        )}
                        {company.phone && (
                          <p className="text-sm text-gray-600 flex items-center gap-1 mt-1">
                            <Phone className="w-3 h-3" />
                            {company.phone}
                          </p>
                        )}
                      </div>
                      <Button
                        onClick={() => deleteItem('companies', company.id)}
                        variant="ghost"
                        size="sm"
                        className="text-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                  {companies.length === 0 && (
                    <p className="text-gray-500 text-center py-8">No companies yet. Add one to get started!</p>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Add New Company</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={createCompany} className="space-y-4">
                  <div>
                    <label className="text-sm font-medium block mb-2">Company Name *</label>
                    <Input
                      placeholder="e.g., Acme Corp"
                      value={newCompany.name}
                      onChange={(e) => setNewCompany({ ...newCompany, name: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium block mb-2">Domain</label>
                    <Input
                      placeholder="e.g., acme.com"
                      value={newCompany.domain}
                      onChange={(e) => setNewCompany({ ...newCompany, domain: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium block mb-2">Phone</label>
                    <Input
                      placeholder="e.g., +1 234 567 8900"
                      value={newCompany.phone}
                      onChange={(e) => setNewCompany({ ...newCompany, phone: e.target.value })}
                    />
                  </div>
                  <Button type="submit" className="w-full bg-purple-600 hover:bg-purple-700">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Company
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Contacts Tab */}
        {activeTab === 'contacts' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle>All Contacts ({contacts.length})</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 max-h-[600px] overflow-auto">
                  {contacts.map((contact) => (
                    <div key={contact.id} className="p-4 border rounded-lg flex items-start justify-between hover:bg-gray-50">
                      <div className="flex-1">
                        <h4 className="font-semibold text-lg">
                          {contact.firstName} {contact.lastName}
                        </h4>
                        {contact.email && (
                          <p className="text-sm text-gray-600 flex items-center gap-1">
                            <Mail className="w-3 h-3" />
                            {contact.email}
                          </p>
                        )}
                        {contact.phone && (
                          <p className="text-sm text-gray-600 flex items-center gap-1">
                            <Phone className="w-3 h-3" />
                            {contact.phone}
                          </p>
                        )}
                        {contact.companyName && (
                          <p className="text-xs text-purple-600 mt-1">{contact.companyName}</p>
                        )}
                        {contact.lifecycleStage && (
                          <span className="inline-block mt-2 px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded-full">
                            {contact.lifecycleStage}
                          </span>
                        )}
                      </div>
                      <Button
                        onClick={() => deleteItem('contacts', contact.id)}
                        variant="ghost"
                        size="sm"
                        className="text-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                  {contacts.length === 0 && (
                    <p className="text-gray-500 text-center py-8">No contacts yet. Add one to get started!</p>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Add New Contact</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={createContact} className="space-y-4">
                  <div>
                    <label className="text-sm font-medium block mb-2">First Name</label>
                    <Input
                      placeholder="John"
                      value={newContact.firstName}
                      onChange={(e) => setNewContact({ ...newContact, firstName: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium block mb-2">Last Name</label>
                    <Input
                      placeholder="Doe"
                      value={newContact.lastName}
                      onChange={(e) => setNewContact({ ...newContact, lastName: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium block mb-2">Email</label>
                    <Input
                      type="email"
                      placeholder="john@example.com"
                      value={newContact.email}
                      onChange={(e) => setNewContact({ ...newContact, email: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium block mb-2">Phone</label>
                    <Input
                      placeholder="+1 234 567 8900"
                      value={newContact.phone}
                      onChange={(e) => setNewContact({ ...newContact, phone: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium block mb-2">Company</label>
                    <select
                      value={newContact.companyId}
                      onChange={(e) => setNewContact({ ...newContact, companyId: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    >
                      <option value="">No company</option>
                      {companies.map((c) => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-sm font-medium block mb-2">Lifecycle Stage</label>
                    <select
                      value={newContact.lifecycleStage}
                      onChange={(e) => setNewContact({ ...newContact, lifecycleStage: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    >
                      <option value="lead">Lead</option>
                      <option value="prospect">Prospect</option>
                      <option value="customer">Customer</option>
                    </select>
                  </div>
                  <Button type="submit" className="w-full bg-pink-600 hover:bg-pink-700">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Contact
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Deals Tab */}
        {activeTab === 'deals' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle>All Deals ({deals.length})</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 max-h-[600px] overflow-auto">
                  {deals.map((deal) => (
                    <div key={deal.id} className="p-4 border rounded-lg flex items-start justify-between hover:bg-gray-50">
                      <div className="flex-1">
                        <h4 className="font-semibold text-lg">{deal.title}</h4>
                        <p className="text-2xl font-bold text-indigo-600 mt-1">
                          ${deal.amount.toLocaleString()} {deal.currency}
                        </p>
                        {deal.pipelineName && (
                          <p className="text-sm text-gray-600 mt-1">Pipeline: {deal.pipelineName}</p>
                        )}
                        {deal.contactName && (
                          <p className="text-sm text-gray-600">Contact: {deal.contactName}</p>
                        )}
                        {deal.companyName && (
                          <p className="text-xs text-purple-600 mt-1">{deal.companyName}</p>
                        )}
                        <span className="inline-block mt-2 px-2 py-1 bg-indigo-100 text-indigo-700 text-xs rounded-full">
                          {deal.stage}
                        </span>
                      </div>
                      <Button
                        onClick={() => deleteItem('deals', deal.id)}
                        variant="ghost"
                        size="sm"
                        className="text-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                  {deals.length === 0 && (
                    <p className="text-gray-500 text-center py-8">No deals yet. Add one to get started!</p>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Add New Deal</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={createDeal} className="space-y-4">
                  <div>
                    <label className="text-sm font-medium block mb-2">Deal Title *</label>
                    <Input
                      placeholder="e.g., Q4 Enterprise Deal"
                      value={newDeal.title}
                      onChange={(e) => setNewDeal({ ...newDeal, title: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium block mb-2">Amount</label>
                    <Input
                      type="number"
                      placeholder="50000"
                      value={newDeal.amount}
                      onChange={(e) => setNewDeal({ ...newDeal, amount: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium block mb-2">Pipeline</label>
                    <select
                      value={newDeal.pipelineId}
                      onChange={(e) => setNewDeal({ ...newDeal, pipelineId: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    >
                      <option value="">Select pipeline</option>
                      {pipelines.map((p) => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-sm font-medium block mb-2">Contact</label>
                    <select
                      value={newDeal.contactId}
                      onChange={(e) => setNewDeal({ ...newDeal, contactId: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    >
                      <option value="">No contact</option>
                      {contacts.map((c) => (
                        <option key={c.id} value={c.id}>{c.firstName} {c.lastName}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-sm font-medium block mb-2">Company</label>
                    <select
                      value={newDeal.companyId}
                      onChange={(e) => setNewDeal({ ...newDeal, companyId: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    >
                      <option value="">No company</option>
                      {companies.map((c) => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                  </div>
                  <Button type="submit" className="w-full bg-indigo-600 hover:bg-indigo-700">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Deal
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Tasks Tab */}
        {activeTab === 'tasks' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle>All Tasks ({tasks.length})</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 max-h-[600px] overflow-auto">
                  {tasks.map((task) => (
                    <div key={task.id} className="p-4 border rounded-lg flex items-start gap-3 hover:bg-gray-50">
                      <input
                        type="checkbox"
                        checked={task.done}
                        onChange={() => toggleTask(task)}
                        className="mt-1 w-5 h-5 text-purple-600"
                      />
                      <div className="flex-1">
                        <h4 className={`font-semibold ${task.done ? 'line-through text-gray-400' : ''}`}>
                          {task.title}
                        </h4>
                        {task.dueDate && (
                          <p className="text-sm text-gray-600 flex items-center gap-1 mt-1">
                            <Calendar className="w-3 h-3" />
                            {new Date(task.dueDate).toLocaleDateString()}
                          </p>
                        )}
                      </div>
                      <Button
                        onClick={() => deleteItem('tasks', task.id)}
                        variant="ghost"
                        size="sm"
                        className="text-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                  {tasks.length === 0 && (
                    <p className="text-gray-500 text-center py-8">No tasks yet. Add one to get started!</p>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Add New Task</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={createTask} className="space-y-4">
                  <div>
                    <label className="text-sm font-medium block mb-2">Task Title *</label>
                    <Input
                      placeholder="e.g., Follow up with client"
                      value={newTask.title}
                      onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium block mb-2">Due Date</label>
                    <Input
                      type="date"
                      value={newTask.dueDate}
                      onChange={(e) => setNewTask({ ...newTask, dueDate: e.target.value })}
                    />
                  </div>
                  <Button type="submit" className="w-full bg-teal-600 hover:bg-teal-700">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Task
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Pipelines Tab */}
        {activeTab === 'pipelines' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle>All Pipelines ({pipelines.length})</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 max-h-[600px] overflow-auto">
                  {pipelines.map((pipeline) => (
                    <div key={pipeline.id} className="p-4 border rounded-lg flex items-center justify-between hover:bg-gray-50">
                      <h4 className="font-semibold text-lg">{pipeline.name}</h4>
                      <Button
                        onClick={() => deleteItem('pipelines', pipeline.id)}
                        variant="ghost"
                        size="sm"
                        className="text-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                  {pipelines.length === 0 && (
                    <p className="text-gray-500 text-center py-8">No pipelines yet. Add one to get started!</p>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Add New Pipeline</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium block mb-2">Pipeline Name *</label>
                    <Input
                      placeholder="e.g., Sales Pipeline"
                      value={newPipelineName}
                      onChange={(e) => setNewPipelineName(e.target.value)}
                    />
                  </div>
                  <Button onClick={createPipeline} className="w-full bg-purple-600 hover:bg-purple-700">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Pipeline
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Bulk Email Tab */}
        {activeTab === 'bulk-email' && (
          <div className="space-y-6">
            {/* Step 1: Gmail Configuration */}
            <Card className="bg-gradient-to-r from-blue-50 to-purple-50 border-2 border-blue-200">
              <CardHeader>
                <CardTitle>📧 Step 1: Gmail Configuration</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium block mb-2">Your Gmail Address *</label>
                    <Input
                      type="email"
                      placeholder="your-email@gmail.com"
                      value={gmailUser}
                      onChange={(e) => setGmailUser(e.target.value)}
                      className="max-w-md"
                    />
                    <p className="text-xs text-gray-500 mt-1">Emails will be sent from this address</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Email Templates */}
              <Card>
                <CardHeader>
                  <CardTitle>✉️ Step 2: Email Templates ({emailTemplates.length})</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="space-y-2 max-h-[300px] overflow-auto">
                      {emailTemplates.map((template) => (
                        <div key={template.id} className="p-3 border rounded-lg hover:bg-gray-50">
                          <div className="flex items-center justify-between mb-2">
                            <h4 className="font-semibold">{template.name}</h4>
                            <Button
                              onClick={async () => {
                                try {
                                  await axios.delete(`${API}/crm/email-templates/${template.id}`, getAuthHeaders());
                                  toast.success('Template deleted');
                                  loadAllData();
                                } catch (error) {
                                  toast.error('Failed to delete template');
                                }
                              }}
                              variant="ghost"
                              size="sm"
                              className="text-red-600 hover:bg-red-50"
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                          <p className="text-xs text-gray-600 mb-1">Subject: {template.subject}</p>
                          <p className="text-xs text-gray-500 line-clamp-2">{template.body}</p>
                        </div>
                      ))}
                      {emailTemplates.length === 0 && (
                        <p className="text-gray-500 text-center py-4">No templates yet</p>
                      )}
                    </div>

                    <div className="space-y-3 pt-4 border-t">
                      <h4 className="font-semibold text-sm">Create New Template</h4>
                      <Input
                        placeholder="Template Name"
                        value={newTemplate.name}
                        onChange={(e) => setNewTemplate({ ...newTemplate, name: e.target.value })}
                      />
                      <Input
                        placeholder="Email Subject (use {{fullName}} for personalization)"
                        value={newTemplate.subject}
                        onChange={(e) => setNewTemplate({ ...newTemplate, subject: e.target.value })}
                      />
                      <textarea
                        className="w-full px-3 py-2 border rounded-lg text-sm"
                        rows="5"
                        placeholder="Email body... Use {{fullName}} where you want the recipient's name. Example: Hey {{fullName}}, how are you? I'm introducing my new business..."
                        value={newTemplate.body}
                        onChange={(e) => setNewTemplate({ ...newTemplate, body: e.target.value })}
                      />
                      <Button
                        onClick={async () => {
                          if (!newTemplate.name || !newTemplate.subject || !newTemplate.body) {
                            toast.error('Please fill all template fields');
                            return;
                          }
                          try {
                            await axios.post(`${API}/crm/email-templates`, newTemplate, getAuthHeaders());
                            toast.success('Template created!');
                            setNewTemplate({ name: '', subject: '', body: '' });
                            loadAllData();
                          } catch (error) {
                            toast.error('Failed to create template');
                          }
                        }}
                        className="w-full bg-blue-600 hover:bg-blue-700"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Create Template
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Select Recipients */}
              <Card>
                <CardHeader>
                  <CardTitle>👥 Step 3: Select Recipients ({selectedContacts.length})</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="bg-blue-50 p-3 rounded-lg">
                      <p className="text-sm font-medium text-blue-900">Your Network Contacts</p>
                      <p className="text-xs text-blue-700 mt-1">{allContacts.length} total contacts available</p>
                    </div>

                    <div className="space-y-2 max-h-[400px] overflow-auto">
                      {allContacts.filter(c => c.email).map((contact) => (
                        <div
                          key={contact.id}
                          className={`p-3 border rounded-lg cursor-pointer transition-all ${
                            selectedContacts.find(c => c.id === contact.id)
                              ? 'bg-purple-50 border-purple-300'
                              : 'hover:bg-gray-50'
                          }`}
                          onClick={() => {
                            if (selectedContacts.find(c => c.id === contact.id)) {
                              setSelectedContacts(selectedContacts.filter(c => c.id !== contact.id));
                            } else {
                              setSelectedContacts([...selectedContacts, contact]);
                            }
                          }}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <h4 className="font-semibold text-sm">{contact.name || 'No Name'}</h4>
                              <p className="text-xs text-gray-600">{contact.email}</p>
                              {contact.businessName && (
                                <p className="text-xs text-gray-500">{contact.businessName}</p>
                              )}
                            </div>
                            <input
                              type="checkbox"
                              checked={!!selectedContacts.find(c => c.id === contact.id)}
                              readOnly
                              className="mt-1"
                            />
                          </div>
                        </div>
                      ))}
                      {allContacts.filter(c => c.email).length === 0 && (
                        <p className="text-gray-500 text-center py-4">No contacts with email addresses</p>
                      )}
                    </div>

                    <div className="flex gap-2 pt-2 border-t">
                      <Button
                        onClick={() => setSelectedContacts(allContacts.filter(c => c.email))}
                        variant="outline"
                        size="sm"
                        className="flex-1"
                      >
                        Select All
                      </Button>
                      <Button
                        onClick={() => setSelectedContacts([])}
                        variant="outline"
                        size="sm"
                        className="flex-1"
                      >
                        Clear
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Send Bulk Email */}
            <Card className="bg-gradient-to-r from-green-50 to-blue-50 border-2 border-green-200">
              <CardHeader>
                <CardTitle>🚀 Step 4: Send Bulk Email</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium block mb-2">Select Template</label>
                      <select
                        className="w-full px-3 py-2 border rounded-lg"
                        onChange={(e) => {
                          const templateId = e.target.value;
                          if (templateId) {
                            const template = emailTemplates.find(t => t.id === templateId);
                            if (template) {
                              // Show preview
                              const previewName = selectedContacts[0]?.name || 'John Smith';
                              toast.info(
                                `Preview: ${template.subject.replace('{{fullName}}', previewName)}\n\n${template.body.replace('{{fullName}}', previewName).substring(0, 100)}...`,
                                { duration: 5000 }
                              );
                            }
                          }
                        }}
                      >
                        <option value="">Choose a template...</option>
                        {emailTemplates.map((template) => (
                          <option key={template.id} value={template.id}>
                            {template.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="flex items-end">
                      <Button
                        onClick={async () => {
                          const templateSelect = document.querySelector('select');
                          const templateId = templateSelect.value;
                          
                          if (!gmailUser) {
                            toast.error('Please enter your Gmail address');
                            return;
                          }
                          if (!templateId) {
                            toast.error('Please select a template');
                            return;
                          }
                          if (selectedContacts.length === 0) {
                            toast.error('Please select at least one recipient');
                            return;
                          }
                          
                          if (!window.confirm(`Send personalized emails to ${selectedContacts.length} recipients?`)) {
                            return;
                          }

                          try {
                            toast.loading('Sending emails...', { id: 'bulk-email' });
                            
                            const response = await axios.post(`${API}/crm/send-bulk-email`, {
                              templateId,
                              recipients: selectedContacts,
                              gmailUser
                            }, getAuthHeaders());
                            
                            if (response.data.success) {
                              toast.success(
                                `✅ ${response.data.sent} emails sent successfully!`,
                                { id: 'bulk-email', duration: 5000 }
                              );
                              setSelectedContacts([]);
                            } else {
                              toast.error(response.data.message, { id: 'bulk-email' });
                            }
                          } catch (error) {
                            console.error('Bulk email error:', error);
                            toast.error('Failed to send emails. Check your Gmail settings.', { id: 'bulk-email' });
                          }
                        }}
                        className="w-full bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700 text-white"
                        disabled={!gmailUser || emailTemplates.length === 0 || selectedContacts.length === 0}
                      >
                        <Mail className="w-4 h-4 mr-2" />
                        Send to {selectedContacts.length} Contact{selectedContacts.length !== 1 ? 's' : ''}
                      </Button>
                    </div>
                  </div>

                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <p className="text-sm text-yellow-900">
                      <strong>📝 Personalization:</strong> Use <code className="bg-yellow-200 px-1 rounded">{'{{fullName}}'}</code> in your template.
                      For example: "Hey {'{{fullName}}'}, how are you?" will become "Hey Sam, how are you?" when sent to Sam.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {/* Upgrade Modal */}
      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        feature="crm_accounts"
        currentLimit={limit}
      />
      
      {/* PWA Install Prompt */}
      <PWAInstallPrompt
        appName="DigiCRM"
        appIcon="📊"
        appColor="#14b8a6"
        storageKey="digicrm-install-prompt"
      />
    </div>
  );
}
